<template>
  <div :class="{'hidden': !isShown}">
    <div class="main-container">
      <div class="header-container">
        <span class="font-semibold text-xl">
          List of
          <span class="capitalize">
            {{ shipType === DOCKING_BARGE_TYPE() ? 'Docked Barge' : shipType }}
          </span>
        </span>
        <div
          class="ml-auto p-1 flex items-center justify-center bg-background rounded-md cursor-pointer"
          @click="$emit('update:isShown', false)"
        >
          <vs-icon
            icon="close"
            color="primary"
            size="small"
          />
        </div>
      </div>
      <div class="body-container">
        <vs-input
          icon-no-border
          class="border border-secondary rounded-md mb-3"
          v-model="search"
          icon="search"
          placeholder="Search here..."
        />
        <div
          v-if="tabShown"
          class="active-tabs"
        >
          <div
            v-for="(item, index) in tabs"
            class="tab-body"
            :key="index"
            :class="{
              'active': activeTab === item.value,
              'inactive': activeTab !== item.value
            }"
            @click="() => {
              $emit('update:shipType', item.value)
              activeTab = item.value
            }"
          >
            <span
              class="mx-auto w-full text-center"
              :class="{'text-white font-semibold': activeTab === item.value}"
            >
              {{ item.text }}
            </span>
          </div>
        </div>
        <div v-if="shipType === 'docking_barge'">
          <div class="mb-5" />
        </div>
        <div class="accordion-container">
          <accordion
            v-if="filteredShips.length > 0"
            class="accordion-parent"
          >
            <template v-for="(item, index) in filteredShips">
              <accordion-item
                :key="`parent-${index}-${item.id}`"
                :has-child="true"
                class="accordion-parent-item"
                @opened="handleOpen"
                @closed="handleClose"
                :active-id="defaultId || selectedVesselId"
                :id="item.id"
              >
                <template #accordion-trigger>
                  <div class="flex flex-row items-center gap-2">
                    <template>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M12.858 20H10.221C6.3456 20 4.40789 20 3.20394 18.8284C2 17.6569 2 15.7712 2 12C2 8.22876 2 6.34315 3.20394 5.17157C4.40789 4 6.34561 4 10.221 4H12.858C15.0854 4 16.1992 4 17.1289 4.50143C18.0586 5.00286 18.6488 5.92191 19.8294 7.76001L20.5102 8.82001C21.5034 10.3664 22 11.1396 22 12C22 12.8604 21.5034 13.6336 20.5102 15.18L19.8294 16.24C18.6488 18.0781 18.0586 18.9971 17.1289 19.4986C16.1992 20 15.0854 20 12.858 20ZM7 7.05423C7.41421 7.05423 7.75 7.37026 7.75 7.76011V16.2353C7.75 16.6251 7.41421 16.9412 7 16.9412C6.58579 16.9412 6.25 16.6251 6.25 16.2353V7.76011C6.25 7.37026 6.58579 7.05423 7 7.05423Z"
                          :fill="item.color"
                        />
                      </svg>
                    </template>
                    <div>
                      <span class="font-semibold">
                        {{
                          shipType === VESSEL_TYPE()
                            ? `${item.vessel_name}`
                            : shipType === TUG_TYPE() || shipType === DOCKING_BARGE_TYPE()
                              ? `${item.tugboat_name} - ${item.barge_name}`
                              : 'TO BE CHANGED'
                        }}
                      </span>
                    </div>
                  </div>
                </template>
                <template #accordion-content>
                  <accordion-item
                    :key="`general-${index}`"
                    :is-child="true"
                    :icon-more="'expand_more'"
                    :icon-less="'expand_less'"
                    class="accordion-child"
                    :id="item.id * 10"
                  >
                    <template #accordion-trigger>
                      <span class="font-semibold">
                        General Information
                      </span>
                    </template>
                    <template #accordion-content>
                      <general-information
                        :ship-type="shipType"
                        :data="item"
                        @viewVesselDetail="viewVesselDetail"
                      />
                    </template>
                  </accordion-item>
                  <accordion-item
                    :key="`details-${index}`"
                    :is-child="true"
                    :icon-more="'expand_more'"
                    :icon-less="'expand_less'"
                    class="accordion-child"
                    :id="item.id * 10 + 1"
                    v-if="shipType === TUG_TYPE() || shipType === DOCKING_BARGE_TYPE()"
                  >
                    <template #accordion-trigger>
                      <span class="font-semibold">
                        Details
                      </span>
                    </template>
                    <template #accordion-content>
                      <Details
                        :data="item"
                      />
                    </template>
                  </accordion-item>
                  <accordion-item
                    :key="`position-${index}`"
                    :is-child="true"
                    :icon-more="'expand_more'"
                    :icon-less="'expand_less'"
                    class="accordion-child"
                    :id="item.id * 10 + 2"
                    v-if="shipType === TUG_TYPE()"
                  >
                    <template #accordion-trigger>
                      <span class="font-semibold">
                        Barge Position
                      </span>
                    </template>
                    <template #accordion-content>
                      <barge-position
                        v-if="item.is_blu_available"
                        :data="item"
                      />
                      <div
                        v-else
                        class="text-center"
                      >
                        <span class="text-grey-001"> No data found for this trip </span>
                      </div>
                    </template>
                  </accordion-item>
                  <template v-if="shipType === VESSEL_TYPE()">
                    <!-- @opened trigger auto open barge detail on accordion click -->
                    <accordion-item
                      v-for="(tugboat, tindex) in item.tugboats"
                      :key="`position-${index}-${tindex}`"
                      :is-child="true"
                      :icon-more="'expand_more'"
                      :icon-less="'expand_less'"
                      class="accordion-child"
                      :id="item.id * 10 + 1 + tindex"
                      @opened="handleOpen(tugboat.id, TUG_TYPE())"
                    >
                      <template #accordion-trigger>
                        <span class="font-semibold">
                          Barge Position
                        </span>
                      </template>
                      <template #accordion-content>
                        <barge-position
                          :parent-type="VESSEL_TYPE()"
                          :data="tugboat"
                          @viewTugBargeDetail="viewTugBargeDetail"
                        />
                      </template>
                    </accordion-item>
                  </template>
                  <accordion-item
                    :key="`operation-${index}`"
                    :is-child="true"
                    :icon-more="'expand_more'"
                    :icon-less="'expand_less'"
                    class="accordion-child"
                    :id="item.id * 10 + 3"
                    v-if="shipType === TUG_TYPE()"
                  >
                    <template #accordion-trigger>
                      <span class="font-semibold">
                        Operation Time
                      </span>
                    </template>
                    <template #accordion-content>
                      <div
                        v-if="!Object.values(item.operation || {}).some(v => v > 0)"
                        class="text-center"
                      >
                        <span class="text-grey-001"> No data found for this trip </span>
                      </div>
                      <operation-time
                        v-else
                        :data="item"
                      />
                    </template>
                  </accordion-item>
                  <accordion-item
                    :key="`delay-time-${index}`"
                    :is-child="true"
                    :item-count="ships.length"
                    :parent-index="index"
                    :icon-more="'expand_more'"
                    :icon-less="'expand_less'"
                    class="accordion-child"
                    :id="item.id * 10 + 4"
                    v-if="shipType === TUG_TYPE()"
                  >
                    <template #accordion-trigger>
                      <span class="font-semibold">
                        Delay Time
                      </span>
                    </template>
                    <template #accordion-content>
                      <div
                        v-if="!item.delay_time"
                        class="text-center"
                      >
                        <span class="text-grey-001"> No data found for this trip </span>
                      </div>
                      <delay-time
                        v-else
                        :data="item"
                      />
                    </template>
                  </accordion-item>
                </template>
              </accordion-item>
            </template>
          </accordion>
          <div
            v-else
            class="text-center"
          >
            <span class="text-grey-001"> No data found </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {debounce} from "lodash";

import Accordion from "@/components/accordion";
import AccordionItem from "@/components/accordionItem";
import BargePosition from "@/components/mapListPopUp/cards/bargePosition";
import DelayTime from "@/components/mapListPopUp/cards/delayTime";
import Details from "@/components/mapListPopUp/cards/details";
import GeneralInformation from "@/components/mapListPopUp/cards/generalInformation";
import OperationTime from "@/components/mapListPopUp/cards/operationTime";
import {DOCKING_BARGE_TYPE, TUG_TYPE, VESSEL_TYPE} from "@/fixtures/Constants";

export default {
  components: {
    Accordion,
    AccordionItem,
    GeneralInformation,
    Details,
    BargePosition,
    OperationTime,
    DelayTime
  },
  props: {
    selectedVesselId: {
      type: Number,
      default: () => null
    },
    isShown: {
      type: Boolean,
      default: () => false
    },
    shipType: {
      type: String,
      default: () => 'vessel'
    },
    detailType: {
      type: String,
      default: () => null
    },
    detail: {
      type: Object,
      default: () => {}
    },
    ships: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      search: null,
      activeTab: null,
      defaultId: null,
      tabs: [
        { value: TUG_TYPE, text: 'Barge Information' },
        { value: VESSEL_TYPE, text: 'Vessel Information' }
      ],
      currentId: null,
      explicitlyExpandedIds: new Set()
    }
  },
  computed: {
    tabShown() {
      return this.shipType !== 'docking_barge'
    },
    filteredShips() {
      if (this.shipType === 'docking_barge') {
        return this.ships.filter(ship => ship.is_docking === true)
      }
      if (this.shipType === TUG_TYPE) {
        return this.ships.filter(ship => ship.is_docking !== true)
      }
      return this.ships
    }
  },
  watch: {
    shipType: {
      handler(val) {
        if (!!val) {
          this.activeTab = this.tabs.find((item) => item.value === val)?.value || val
        }
      }
    },
    detailType: {
      handler(val) {
        if (!!val) {
          this.activeTab = this.tabs.find((item) => item.value === val)?.value || val
          this.$emit('update:shipType', this.activeTab)
        }
      }
    },
    selectedVesselId: {
      immediate: true,
      handler(val) {
        if(!val) this.defaultId = null
      }
    },
    'detail.id': {
      immediate: true,
      handler(val) {
        if (val && this.currentId !== val) {
          this.currentId = val
          const index = this.ships.findIndex((item) => item.id === this.detail.id)
          if (index !== -1) {
            this.defaultId = this.ships[index].id
            this.explicitlyExpandedIds.clear()
            this.explicitlyExpandedIds.add(this.ships[index].id)
          }
        }
      }
    },
    search: {
      handler(val) {
        if (val !== null && !val.length) {
          this.debouncedEmit({search: null})
          return
        }
        this.debouncedEmit({search: val})
      }
    }
  },
  methods: {
    TUG_TYPE() {
      return TUG_TYPE
    },
    VESSEL_TYPE() {
      return VESSEL_TYPE
    },
    DOCKING_BARGE_TYPE() {
      return DOCKING_BARGE_TYPE
    },
    handleClose(shipId) {
      const selectedShip = this.ships.find(({ id }) => id === shipId)
      if(selectedShip && selectedShip.type === VESSEL_TYPE) {
        this.defaultId = null
        this.$emit('filterByVessel', null)
      }
    },
    handleOpen(shipId, shipType = VESSEL_TYPE) {
      this.explicitlyExpandedIds.add(shipId);
      let ship = null
      if (shipType === VESSEL_TYPE) {
        this.currentId = shipId
        ship = this.ships.find(({ id }) => id === shipId)
        if(ship.type === VESSEL_TYPE) {
          this.$emit('filterByVessel', ship)
        }
      } else {
        const { tugboats } = this.ships.find(({ id }) => id === this.currentId) || {}
        ship = tugboats.find(({ id }) => id === shipId)
      }

      this.$emit('refocus', ship)
    },
    viewTugBargeDetail(shipId) {
      let shipType = TUG_TYPE
      if (this.shipType === VESSEL_TYPE) {
        const currentVessel = this.ships.find(({ id }) => id === this.currentId) || null
        const chosenTugBarge = currentVessel?.tugboats.find(({ id }) => id === shipId) || null
        shipType = chosenTugBarge && chosenTugBarge?.is_docking ? DOCKING_BARGE_TYPE : shipType
      }

      this. $emit('update:shipType', shipType)
      this.$nextTick(() => {
        const ship = this.ships.find(({ id }) => id === shipId)
        this.$emit('refocus', ship)
        this.$emit('update:detailType', shipType)
        this.$emit('update:detail', ship)
      });
    },
    viewVesselDetail(vesselName) {
      this. $emit('update:shipType', VESSEL_TYPE)
      this.$nextTick(() => {
        const ship = this.ships.find(({ vessel_name }) => vessel_name === vesselName)
        this.$emit('refocus', ship)
        this.$emit('update:detailType', VESSEL_TYPE)
        this.$emit('update:detail', ship)
      });
    }
  },
  created() {
    this.debouncedEmit = debounce((payload) => {
      this.$emit('search', payload, this.shipType)
    }, 300)
  }
}
</script>

<style lang="scss" scoped>
.main-container {
  @apply mr-5 mb-5 flex flex-col bg-white rounded-lg;

  height: max-content;
  max-height: calc(100vh - 10rem);
  width: 27rem;
}

.header-container {
  @apply p-5 flex flex-row items-center bg-white rounded-lg;
}

.body-container {
  @apply mt-3 px-5 pb-5 flex flex-col;

  height: calc(100% - 6.5rem);
}

/deep/ .vs-con-input-label {
  @apply w-full;
}

/deep/ .vs-input--input {
  @apply bg-white;
  @apply h-12 rounded-lg;

  padding-left: calc(1rem + 24px) !important;
}

/deep/ .vs-input--placeholder {
  padding-left: calc(1rem + 24px) !important;
}

/deep/ .vs-input--icon {
  top: auto;
  left: 0.5rem;
  color: #FF7F57;
}

.active-tabs {
  @apply mb-5 flex flex-row gap-1 bg-white rounded-lg;
}

.tab-body {
  @apply mt-2 p-3 w-1/2 flex flex-row items-center cursor-pointer;

  &.active {
    @apply bg-primary rounded-t-lg;
  }

  &.inactive {
    @apply text-grey-001 rounded-t-lg;
    background: #F8F8F8;
    box-shadow: 1px 1px 3px 0 rgba(101,70,195,0.39);
  }
}

.accordion-container {
  @apply overflow-auto;
  height: calc(100vh - 25rem);

  .accordion-parent {
    @apply flex flex-col justify-center gap-2;

    &-item {
      @apply w-full;
      @apply rounded-md;

      /deep/ .accordion-trigger {
        @apply p-2;
        @apply rounded-md;
        background: #F0F0F0;
        border: 1px solid #989898;

        &.expanded {
          border-bottom-left-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }
      }

      /deep/ .accordion-content {
        @apply flex flex-col justify-center gap-2;
        @apply p-2;
        @apply rounded-b-md;
        border: 1px solid #989898 !important;

        .accordion-child {
          .accordion-trigger {
            background-color: transparent;

            &.expanded {
              border-bottom: none;
            }
          }

          .accordion-content {
            border-top: none !important;
          }
        }
      }
    }

    /deep/ .vue-recycle-scroller__item-wrapper {
      min-height: fit-content;
    }
  }
}
</style>